/**
 * `/settings/keys` — API key CRUD.
 *
 * Replacement for legacy `KeysIsland.tsx`. Lists active keys, lets
 * admins mint new ones, and revoke any. The secret is revealed
 * exactly once on create — we surface a big warning and a copy
 * button.
 *
 * Write paths gate on the workspace query's `role === 'admin'`
 * (server enforces it too — UI is just cosmetic).
 */
import { ConfirmDialog } from '@/components/primitives/ConfirmDialog';
import { EmptyState } from '@/components/primitives/EmptyState';
import { ErrorState } from '@/components/primitives/ErrorState';
import { PageActions } from '@/components/primitives/PageActions';
import { SigningSecretReveal } from '@/components/primitives/SigningSecretReveal';
import { UpsellInline } from '@/components/primitives/UpsellInline';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { FetchError } from '@/lib/api';
import type { ApiKey, ApiKeyScope } from '@/lib/api-types';
import { toastFromError, toastSuccess } from '@/lib/errors';
import { relativeTime } from '@/lib/format';
import { useCreateKey, useEntitlement, useKeys, useRevokeKey, useWorkspace } from '@/lib/queries';
import { useAuth } from '@clerk/react';
import { Key, Plus, Trash } from '@phosphor-icons/react';
import { createFileRoute } from '@tanstack/react-router';
import { type FormEvent, type ReactElement, useState } from 'react';

export const Route = createFileRoute('/_app/settings/keys')({
  component: KeysPage,
});

const AVAILABLE_SCOPES: { id: ApiKeyScope; label: string; hint: string }[] = [
  {
    id: 'snapshots:write',
    label: 'snapshots:write',
    hint: 'POST /v1/audits — trigger new audits.',
  },
  {
    id: 'snapshots:read',
    label: 'snapshots:read',
    hint: 'Read audit state + download reports.',
  },
  {
    id: 'metrics:read',
    label: 'metrics:read',
    hint: 'p75 / trend / samples metrics endpoints.',
  },
];

function KeysPage(): ReactElement {
  const { orgId } = useAuth();
  const keys = useKeys();
  const workspace = useWorkspace();
  const revoke = useRevokeKey();

  const [createOpen, setCreateOpen] = useState(false);
  const [revealed, setRevealed] = useState<{ secret: string; prefix: string } | null>(null);
  const [toRevoke, setToRevoke] = useState<ApiKey | null>(null);
  const [revoking, setRevoking] = useState(false);

  const activeOrg = workspace.data?.organisations.find((o) => o.auth_provider_id === orgId);
  const isAdmin = activeOrg?.role === 'admin';

  // Pricing-tier gate. Server enforces via 402 on
  // `POST /v1/dashboard/api-keys`, but surfacing the upsell
  // up front avoids "the dashboard 429'd when I tried to create
  // a key" surprise for suspended orgs.
  const keyEntitlement = useEntitlement('create_api_key');
  const canCreateKey = isAdmin && keyEntitlement.allowed;

  async function onRevoke() {
    if (!toRevoke) return;
    setRevoking(true);
    try {
      await revoke.mutateAsync({ uuid: toRevoke.uuid });
      toastSuccess('Key revoked', `${toRevoke.name} can no longer authenticate.`);
      setToRevoke(null);
    } catch (err) {
      toastFromError(err);
    } finally {
      setRevoking(false);
    }
  }

  const active = (keys.data ?? []).filter((k) => !k.revoked_at);

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-[var(--color-ink)]">API keys</h2>
          <p className="text-sm text-[var(--color-mute)]">
            Keys authenticate server-to-server requests. Each carries a prefix (
            <span className="font-mono text-xs">sk_live_…</span>) that's safe to show in logs.
          </p>
        </div>
        {isAdmin && (
          <PageActions>
            {canCreateKey ? (
              <Button onClick={() => setCreateOpen(true)}>
                <Plus aria-hidden="true" />
                Create key
              </Button>
            ) : keyEntitlement.isLoading ? null : (
              <UpsellInline variant="button" minPlan={keyEntitlement.minPlan} />
            )}
          </PageActions>
        )}
      </header>

      {keys.isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </div>
      )}

      {keys.isError && !keys.isLoading && (
        <ErrorState
          title="We couldn't load API keys"
          error={keys.error}
          retry={() => {
            void keys.refetch();
          }}
        />
      )}

      {!keys.isLoading && !keys.isError && active.length === 0 && (
        <EmptyState
          icon={Key}
          title="No keys yet"
          description="Create your first key to run audits from your own tooling."
          action={
            !isAdmin ? undefined : canCreateKey ? (
              <Button onClick={() => setCreateOpen(true)}>
                <Plus aria-hidden="true" />
                Create key
              </Button>
            ) : keyEntitlement.isLoading ? undefined : (
              <UpsellInline variant="button" minPlan={keyEntitlement.minPlan} />
            )
          }
        />
      )}

      {!keys.isLoading && !keys.isError && active.length > 0 && (
        <section className="overflow-hidden rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)]">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-[var(--color-line-soft)] text-left text-xs uppercase tracking-[0.06em] text-[var(--color-mute)]">
                  <th className="px-4 py-2 font-medium">Name</th>
                  <th className="px-4 py-2 font-medium">Prefix</th>
                  <th className="px-4 py-2 font-medium">Scopes</th>
                  <th className="px-4 py-2 font-medium">Last used</th>
                  <th className="px-4 py-2 font-medium">Created</th>
                  <th className="w-16 px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {active.map((k) => (
                  <tr key={k.uuid} className="border-b border-[var(--color-line-soft)]/70">
                    <td className="px-4 py-3 text-sm font-medium text-[var(--color-ink)]">
                      {k.name}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-[var(--color-ink-soft)]">
                      {k.prefix}•••
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {k.scopes.map((s) => (
                          <span
                            key={s}
                            className="rounded-full bg-[var(--color-paper-alt)] px-1.5 py-0.5 font-mono text-[10px] text-[var(--color-ink-soft)]"
                          >
                            {s}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-[var(--color-mute)]">
                      {relativeTime(k.last_used_at)}
                    </td>
                    <td className="px-4 py-3 text-xs text-[var(--color-mute)]">
                      {relativeTime(k.created_at)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {isAdmin && (
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label={`Revoke ${k.name}`}
                          onClick={() => setToRevoke(k)}
                        >
                          <Trash aria-hidden="true" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <CreateKeyDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(k) => setRevealed({ secret: k.secret, prefix: k.key.prefix })}
      />

      <SigningSecretReveal
        secret={revealed?.secret ?? null}
        onClose={() => setRevealed(null)}
        title="Copy your new key"
        description="This is the only time we'll show this secret. Store it somewhere safe — treat it like a password."
        codeLabel="Secret (one-time)"
        meta={
          revealed && (
            <>
              The prefix <span className="font-mono">{revealed.prefix}</span> identifies this key in
              logs — it's safe to commit alongside your code.
            </>
          )
        }
      />

      <ConfirmDialog
        open={toRevoke !== null}
        onOpenChange={(next) => {
          if (!next) setToRevoke(null);
        }}
        title="Revoke this key?"
        description={
          toRevoke
            ? `${toRevoke.name} will stop working immediately. Anything using it will start seeing 401s.`
            : undefined
        }
        confirmLabel="Revoke"
        destructive
        loading={revoking}
        onConfirm={onRevoke}
      />
    </div>
  );
}

function CreateKeyDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  onCreated: (response: {
    key: { prefix: string };
    secret: string;
  }) => void;
}): ReactElement {
  const mutation = useCreateKey();
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<ApiKeyScope[]>([
    'snapshots:write',
    'snapshots:read',
    'metrics:read',
  ]);
  const [formError, setFormError] = useState<string | undefined>(undefined);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(undefined);
    if (!name.trim()) {
      setFormError('Give the key a descriptive name.');
      return;
    }
    if (scopes.length === 0) {
      setFormError('Pick at least one scope.');
      return;
    }
    try {
      const res = await mutation.mutateAsync({
        name: name.trim(),
        scopes,
      });
      onCreated(res);
      setName('');
      onOpenChange(false);
    } catch (err) {
      if (err instanceof FetchError) setFormError(err.message);
      else toastFromError(err);
    }
  }

  function toggleScope(id: ApiKeyScope) {
    setScopes((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create API key</DialogTitle>
          <DialogDescription>
            Name it for the service that'll use it — something like "CI/CD" or "Production worker".
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} noValidate>
          <DialogPanel>
            <div className="space-y-4">
              <Field>
                <FieldLabel htmlFor="key-name">Name</FieldLabel>
                <Input
                  id="key-name"
                  placeholder="CI/CD"
                  value={name}
                  onChange={(e) => setName((e.target as HTMLInputElement).value)}
                  autoFocus
                  required
                />
              </Field>
              <div className="space-y-2">
                <div className="text-sm font-medium text-[var(--color-ink)]">Scopes</div>
                <ul className="space-y-2">
                  {AVAILABLE_SCOPES.map((s) => {
                    const checked = scopes.includes(s.id);
                    return (
                      <li
                        key={s.id}
                        className="flex items-start gap-3 rounded-lg border border-[var(--color-line)] p-3"
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => toggleScope(s.id)}
                          aria-label={s.label}
                        />
                        <div className="flex-1">
                          <div className="font-mono text-xs text-[var(--color-ink)]">{s.label}</div>
                          <div className="text-xs text-[var(--color-mute)]">{s.hint}</div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
              {formError && <FieldError>{formError}</FieldError>}
            </div>
          </DialogPanel>
          <DialogFooter>
            <DialogClose render={<Button variant="outline">Cancel</Button>} />
            <Button type="submit" loading={mutation.isPending}>
              Create key
            </Button>
          </DialogFooter>
        </form>
      </DialogPopup>
    </Dialog>
  );
}
